/**
 * Invoice Service
 * Handles API communication for invoice operations including overdue management
 * US-INV-004: View Overdue Invoices
 */

import type {
  OverdueInvoiceSummary,
  OverdueInvoiceFilters,
  OverdueSummary,
  CollectionReport,
  SendReminderResponse,
  PagedOverdueResponse,
  RecordPaymentRequest,
  ExportFormat,
  InvoiceDetails,
  RecordPaymentResponse
} from '@/types/invoice';

const COMMERCE_SERVICE_BASE_URL = import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get headers with authentication
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;
  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

/**
 * Invoice Service API methods
 */
export const invoiceService = {
  /**
   * Get overdue invoices with filters
   * GET /api/v1/invoices/overdue
   *
   * @param filters - Filter criteria
   * @returns Paginated list of overdue invoices
   */
  async getOverdueInvoices(filters?: OverdueInvoiceFilters): Promise<PagedOverdueResponse> {
    const queryParams = new URLSearchParams();

    if (filters) {
      if (filters.customerId) queryParams.append('customerId', String(filters.customerId));
      if (filters.minAmount !== undefined) queryParams.append('minAmount', String(filters.minAmount));
      if (filters.maxAmount !== undefined) queryParams.append('maxAmount', String(filters.maxAmount));
      if (filters.minDaysOverdue !== undefined) queryParams.append('minDaysOverdue', String(filters.minDaysOverdue));
      if (filters.maxDaysOverdue !== undefined) queryParams.append('maxDaysOverdue', String(filters.maxDaysOverdue));
      if (filters.location) queryParams.append('location', filters.location);
      if (filters.page !== undefined) queryParams.append('page', String(filters.page));
      if (filters.size !== undefined) queryParams.append('size', String(filters.size));
    }

    const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/overdue${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch overdue invoices: ${response.status} ${errorText}`);
    }

    return response.json();
  },

  /**
   * Get overdue summary statistics for dashboard
   * GET /api/v1/invoices/overdue/summary
   *
   * @returns Summary statistics
   */
  async getOverdueSummary(): Promise<OverdueSummary> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/overdue/summary`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch overdue summary: ${response.status} ${errorText}`);
    }

    return response.json();
  },

  /**
   * Get collection report with aging analysis
   * GET /api/v1/invoices/overdue/report
   *
   * @returns Collection report with customer and aging data
   */
  async getCollectionReport(): Promise<CollectionReport> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/overdue/report`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch collection report: ${response.status} ${errorText}`);
    }

    return response.json();
  },

  /**
   * Send reminder email for overdue invoice
   * POST /api/v1/invoices/{invoiceId}/remind
   *
   * @param invoiceId - ID of the invoice
   * @returns Response with send status
   */
  async sendReminderEmail(invoiceId: number): Promise<SendReminderResponse> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/overdue/${invoiceId}/remind`,
      {
        method: 'POST',
        headers: getHeaders(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send reminder email: ${response.status} ${errorText}`);
    }

    return response.json();
  },

  /**
   * Export overdue report to PDF or Excel
   * GET /api/v1/invoices/overdue/export?format={format}
   *
   * @param format - Export format (pdf or excel)
   * @returns Blob of the exported file
   */
  async exportOverdueReport(format: ExportFormat): Promise<Blob> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/overdue/export/${format}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders(),
          'Accept': 'application/octet-stream',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to export report: ${response.status} ${errorText}`);
    }

    const blob = await response.blob();

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    a.download = `overdue-report-${timestamp}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return blob;
  },

  /**
   * Get invoice details by ID
   * GET /api/v1/invoices/{invoiceId}
   *
   * @param invoiceId - ID of the invoice
   * @returns Full invoice details
   */
  async getInvoiceDetails(invoiceId: number): Promise<InvoiceDetails> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/${invoiceId}`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch invoice details: ${response.status} ${errorText}`);
    }

    return response.json();
  },

  /**
   * Record payment for invoice
   * POST /api/v1/invoices/{invoiceId}/payments
   *
   * @param invoiceId - ID of the invoice
   * @param paymentData - Payment details
   * @returns Updated invoice with new payment
   */
  async recordPayment(invoiceId: number, paymentData: RecordPaymentRequest): Promise<RecordPaymentResponse> {
    const response = await fetch(
      `${COMMERCE_SERVICE_BASE_URL}/api/v1/invoices/${invoiceId}/payments`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(paymentData),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to record payment: ${response.status} ${errorText}`);
    }

    return response.json();
  },
};
